import React, { useCallback, useMemo, useState } from 'react';

import isEqual from 'lodash/isEqual';
import throttle from 'lodash/throttle';
import { useBoolean } from 'react-use';

import {
  manualTimeUpdateEvent,
  MANUAL_TIME_UPDATE_EVENT_TYPE,
  FONT_SIZE_TO_OUTLINE_WIDTH_RATIO,
  FONT_SIZE_TO_LETTER_SPACING_RATIO,
} from 'components/b2c/Mp3KbpPlayer/Mp3KbpPlayer.constants';
import {
  getCurrentTimeInSong,
  getPixelSizeNumberFromSizingFactor,
} from 'components/b2c/Mp3KbpPlayer/Mp3KbpPlayer.utils';
import { SECONDS_TO_MS_RATIO } from 'services/helpers/formatTime';
import { useMediaElementListener } from 'services/hooks/useMediaElementListener';
import { useVideoIsPlaying } from 'services/hooks/useVideoIsPlaying';
import { Lyrics as LyricsType } from 'shared-types/lyrics';
import { SwipeColors } from 'types/swipeColors';

import { LyricsContainer, LyricsContent, SIZING_FACTORS } from './Lyrics.style';
import { findCurrentBlocksIndexes, shouldDisplayLyrics } from './Lyrics.utils';
import Block from '../Block';
import { MENTION_SLIDE_END_TIME_IN_SECONDS } from '../MentionSlide/MentionSlide.utils';

const CURRENT_BLOCK_SEARCH_MAX_FREQUENCY_IN_MS = 250;

interface Props {
  videoElement: HTMLVideoElement;
  lyrics: LyricsType;
  videoAreaHeight: number;
  lastManualUpdateTimeInMs: number;
  isPremium: boolean;
  swipeColors: SwipeColors;
}

const Lyrics: React.FC<Props> = ({
  videoElement,
  lyrics,
  videoAreaHeight,
  lastManualUpdateTimeInMs,
  isPremium,
  swipeColors,
}) => {
  const [currentBlocksIndexes, setCurrentBlocksIndexes] = useState<number[]>([]);
  const [isVisible, setIsVisible] = useBoolean(
    shouldDisplayLyrics(getCurrentTimeInSong(videoElement, isPremium) * SECONDS_TO_MS_RATIO),
  );
  const [isPreviewTimeUpdateDispatched, setIsPreviewTimeUpdateDispatched] = useBoolean(false);

  const isPlaying = useVideoIsPlaying(videoElement);

  const { fontSize, wordOutlineFontSize, letterSpacing } = useMemo(() => {
    const fontSize = getPixelSizeNumberFromSizingFactor(
      videoAreaHeight,
      SIZING_FACTORS.lyricsFontSize,
    );
    const wordOutlineFontSize = fontSize * FONT_SIZE_TO_OUTLINE_WIDTH_RATIO;
    const letterSpacing = fontSize * FONT_SIZE_TO_LETTER_SPACING_RATIO;

    return {
      fontSize: Math.round(fontSize),
      wordOutlineFontSize: Math.round(wordOutlineFontSize),
      letterSpacing: Math.round(letterSpacing),
    };
  }, [videoAreaHeight]);

  const cursorAreaWidthInPx = useMemo(
    () => getPixelSizeNumberFromSizingFactor(videoAreaHeight, SIZING_FACTORS.cursorAreaWidth),
    [videoAreaHeight],
  );
  const cursorAreaRightSpacingInPx = useMemo(
    () =>
      getPixelSizeNumberFromSizingFactor(videoAreaHeight, SIZING_FACTORS.cursorAreaRightSpacing),
    [videoAreaHeight],
  );

  const updateCurrentBlockIndexes = useCallback(
    throttle((currentTime: number) => {
      const nextBlockIndexes = findCurrentBlocksIndexes(lyrics.blocks, currentTime);
      if (!isEqual(currentBlocksIndexes, nextBlockIndexes)) {
        setCurrentBlocksIndexes(nextBlockIndexes);
      }
    }, CURRENT_BLOCK_SEARCH_MAX_FREQUENCY_IN_MS),
    [lyrics.blocks, currentBlocksIndexes, setCurrentBlocksIndexes],
  );

  useMediaElementListener(videoElement, MANUAL_TIME_UPDATE_EVENT_TYPE, () => {
    if (
      isPreviewTimeUpdateDispatched &&
      !isPremium &&
      getCurrentTimeInSong(videoElement, isPremium) < MENTION_SLIDE_END_TIME_IN_SECONDS
    ) {
      setIsPreviewTimeUpdateDispatched(false);
    }
    setCurrentBlocksIndexes([]);
  });

  useMediaElementListener(videoElement, 'timeupdate', () => {
    const currentTimeInSong = getCurrentTimeInSong(videoElement, isPremium);
    if (
      currentTimeInSong > MENTION_SLIDE_END_TIME_IN_SECONDS &&
      isPreviewTimeUpdateDispatched === false &&
      isPremium === false
    ) {
      setIsPreviewTimeUpdateDispatched(true);
      // If it is a preview video a manualtimeupdate event is dispatched after 2 seconds to update
      // the swipe on the first lyrics
      videoElement.dispatchEvent(manualTimeUpdateEvent);
    }
    updateCurrentBlockIndexes(currentTimeInSong);
    setIsVisible(
      shouldDisplayLyrics(getCurrentTimeInSong(videoElement, isPremium) * SECONDS_TO_MS_RATIO),
    );
  });

  return (
    <LyricsContainer isVisible={isVisible}>
      <LyricsContent fontSize={fontSize} letterSpacing={letterSpacing}>
        {currentBlocksIndexes.map((blockIndex) => {
          const block = lyrics.blocks[blockIndex];
          return (
            <Block
              key={blockIndex}
              block={block}
              blockIndex={blockIndex}
              isPlaying={isPlaying}
              lastManualUpdateTimeInMs={lastManualUpdateTimeInMs}
              swipeColors={swipeColors}
              wordOutlineFontSize={wordOutlineFontSize}
              cursorAreaWidthInPx={cursorAreaWidthInPx}
              cursorAreaRightSpacingInPx={cursorAreaRightSpacingInPx}
            />
          );
        })}
      </LyricsContent>
    </LyricsContainer>
  );
};

export default Lyrics;
